import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { Message, MessageStatus } from '@/types/message.types';
import { formatMessageTime } from '@/utils/message';

interface MessageState {
  messages: Message[];
  messagesMap: Map<Message['id'], Message>;
  hasUnreadAgentMessages: boolean;
  lastAgentMessageId: Message['id'] | null;
  lastReadAgentMessageId: Message['id'] | null;
  visibleMessageGroups: Message[][];
  dateDividerIndexes: Set<Message['id']>;

  actions: {
    setMessages: (messages: Message[]) => void;
    prependMessages: (messages: Message[]) => void;
    appendMessage: (message: Message) => void;
    updateMessage: (
      messageId: Message['id'],
      updates: Partial<Message>,
    ) => void;
    updateMessageStatus: (
      messageId: Message['id'],
      status: MessageStatus,
    ) => void;
    moveMessageToEnd: (messageId: Message['id']) => void;
    deleteMessage: (messageId: Message['id']) => void;
    clearMessages: () => void;
    setLastReadAgentMessageId: (messageId: Message['id'] | null) => void;
    getStorageKey: (widgetConfigId: string, companyId: string) => string;
  };
}

// skip submitted contact form message from end-user (501)
// that should only be rendered from sleekflow app
const isNotContactFormSubmissionMessage = (message: Message) =>
  message.extendedMessagePayload?.extendedMessageType !== 501 ||
  message.isSentFromSleekflow;

const getVisibleMessageGroups = (messages: Message[]) => {
  const visibleMessages = messages.filter(isNotContactFormSubmissionMessage);

  // group visible messages by direction and time
  const visibleMessageGroups = visibleMessages.reduce(
    (acc: Message[][], message: Message) => {
      const lastGroup = acc[acc.length - 1];
      const lastMessage = lastGroup?.[lastGroup.length - 1];
      const sameDirection =
        message.isSentFromSleekflow === lastMessage?.isSentFromSleekflow;
      const sameTime =
        formatMessageTime(message.timestamp) ===
        formatMessageTime(lastMessage?.timestamp);

      if (sameDirection && sameTime) {
        // add to existing group
        lastGroup.push(message);
        return acc;
      } else {
        // create new group
        return [...acc, [message]];
      }
    },
    [],
  );

  return visibleMessageGroups;
};

const getDateDividerIndexes = (visibleMessageGroups: Message[][]) => {
  const dateDividerIndexes = new Set<Message['id']>();
  const seenDates = new Set<string>();

  visibleMessageGroups.forEach((messageGroup) => {
    const pilotMessage = messageGroup[0];

    if (pilotMessage && pilotMessage.timestamp) {
      const date = new Date(pilotMessage.timestamp * 1000).toDateString();

      if (!seenDates.has(date)) {
        seenDates.add(date);
        dateDividerIndexes.add(pilotMessage.id);
      }
    }
  });

  return dateDividerIndexes;
};

export const useMessagesStore = create<MessageState>()(
  persist(
    (set) => ({
      messages: [],
      messagesMap: new Map(),
      lastAgentMessageId: null,
      lastReadAgentMessageId: null,
      hasUnreadAgentMessages: false,
      visibleMessageGroups: [],
      dateDividerIndexes: new Set(),

      actions: {
        setMessages: (messages) => {
          const reversedMessages = [...messages].reverse();
          const newMessagesMap = new Map();

          reversedMessages.forEach((message) => {
            newMessagesMap.set(message.id, message);
          });

          set({
            messages: reversedMessages,
            messagesMap: newMessagesMap,
          });
        },

        prependMessages: (messages) => {
          set((state) => {
            // dedup first
            const deduplicatedMessages = messages.filter(
              (message) =>
                !state.messagesMap.has(message.id) &&
                !state.messagesMap.has(message.messageChecksum),
            );
            const reversedMessages = [...deduplicatedMessages].reverse();
            const newMessages = [...reversedMessages, ...state.messages];
            const newMessagesMap = new Map(state.messagesMap);

            deduplicatedMessages.forEach((message) => {
              newMessagesMap.set(message.id, message);
            });

            return {
              messages: newMessages,
              messagesMap: newMessagesMap,
            };
          });
        },

        appendMessage: (message) =>
          set((state) => {
            // dedup first
            if (
              state.messagesMap.has(message.id) ||
              state.messagesMap.has(message.messageChecksum)
            ) {
              return state;
            }

            const newMessages = [...state.messages, message];
            const newMessagesMap = new Map(state.messagesMap);

            newMessagesMap.set(message.id, message);
            return {
              messages: newMessages,
              messagesMap: newMessagesMap,
            };
          }),

        updateMessage: (messageId, updates) =>
          set((state) => {
            const messageToUpdate = state.messagesMap.get(messageId);

            if (!messageToUpdate) {
              console.warn(`Message with id ${messageId} not found`);
              return state;
            }

            const hasChanges = Object.keys(updates).some(
              (key) =>
                messageToUpdate[key as keyof Message] !==
                updates[key as keyof Message],
            );

            if (!hasChanges) {
              return state;
            }

            const updatedMessage = { ...messageToUpdate, ...updates };
            const messageToUpdateIndex = state.messages.findIndex(
              (message) => message.id === messageId,
            );
            const newMessages = [...state.messages];
            newMessages[messageToUpdateIndex] = updatedMessage;
            const newMessagesMap = new Map(state.messagesMap);
            newMessagesMap.set(messageId, updatedMessage);

            return {
              messages: newMessages,
              messagesMap: newMessagesMap,
            };
          }),

        updateMessageStatus: (messageId, status) =>
          set((state) => {
            const messageToUpdate = state.messagesMap.get(messageId);
            if (!messageToUpdate) {
              console.warn(`Message with id ${messageId} not found`);
              return state;
            }

            const hasChanges = messageToUpdate.status !== status;

            if (!hasChanges) {
              return state;
            }

            const updatedMessage = { ...messageToUpdate, status };
            const messageToUpdateIndex = state.messages.findIndex(
              (message) => message.id === messageId,
            );
            const newMessages = [...state.messages];
            newMessages[messageToUpdateIndex] = updatedMessage;
            const newMessagesMap = new Map(state.messagesMap);
            newMessagesMap.set(messageId, updatedMessage);

            return {
              messages: newMessages,
              messagesMap: newMessagesMap,
            };
          }),

        moveMessageToEnd: (messageId) =>
          set((state) => {
            const messageToMove = state.messagesMap.get(messageId);
            if (!messageToMove) {
              console.warn(`Message with id ${messageId} not found`);
              return state;
            }

            const newMessages = [
              ...state.messages.filter((message) => message.id !== messageId),
              messageToMove,
            ];

            return {
              messages: newMessages,
            };
          }),

        deleteMessage: (messageId) =>
          set((state) => {
            const newMessages = state.messages.filter(
              (message) => message.id !== messageId,
            );
            const newMessagesMap = new Map(state.messagesMap);
            newMessagesMap.delete(messageId);

            return {
              messages: newMessages,
              messagesMap: newMessagesMap,
            };
          }),

        clearMessages: () =>
          set({
            messages: [],
            messagesMap: new Map(),
            lastAgentMessageId: null,
            lastReadAgentMessageId: null,
            hasUnreadAgentMessages: false,
            visibleMessageGroups: [],
            dateDividerIndexes: new Set(),
          }),

        setLastReadAgentMessageId: (messageId) =>
          set({ lastReadAgentMessageId: messageId }),

        getStorageKey: (widgetConfigId: string, companyId: string) => {
          return `sf-live-chat-messages-${widgetConfigId}-${companyId}`;
        },
      },
    }),
    {
      name: 'sf-live-chat-messages',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        lastReadAgentMessageId: state.lastReadAgentMessageId,
      }),
    },
  ),
);

useMessagesStore.subscribe((state, prevState) => {
  // TODO: current reddot implementation is purely frontend
  // the ideal way is backend have a flag of whether there is unread agent messages or not
  if (
    state.lastReadAgentMessageId !== prevState.lastReadAgentMessageId ||
    state.lastAgentMessageId !== prevState.lastAgentMessageId
  ) {
    if (!state.lastReadAgentMessageId || !state.lastAgentMessageId) {
      return false;
    }

    useMessagesStore.setState({
      hasUnreadAgentMessages:
        state.lastAgentMessageId !== state.lastReadAgentMessageId,
    });
  }

  // update visibleMessageGroups and date separator indexes when messages change
  if (state.messages !== prevState.messages) {
    const visibleMessageGroups = getVisibleMessageGroups(state.messages);
    const dateDividerIndexes = getDateDividerIndexes(visibleMessageGroups);

    // TODO: may have performance drawback
    // trade off with the actual number of messages fetched & sent in order to call fetch new page on a accurate offset
    // now derived to a visible messages for rendering purpose
    // in order to prepare for the future contact form submission rendering
    useMessagesStore.setState({ visibleMessageGroups, dateDividerIndexes });
  }

  // update lastAgentMessageId when messages length change (only when new messages are fetched/sent)
  if (state.messages.length !== prevState.messages.length) {
    useMessagesStore.setState({
      lastAgentMessageId:
        state.messages.findLast((message) => message.isSentFromSleekflow)?.id ||
        null,
    });
  }
});

export const useMessagesStoreHydration = () => {
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const unsubHydrate = useMessagesStore.persist.onHydrate(() => {
      setHydrated(false);
    });

    const unsubFinishHydration = useMessagesStore.persist.onFinishHydration(
      () => {
        setHydrated(true);
      },
    );

    setHydrated(useMessagesStore.persist.hasHydrated());

    return () => {
      unsubHydrate();
      unsubFinishHydration();
    };
  }, []);

  return hydrated;
};
